// components/models/TopazVideoUpscaleControlPanel.tsx
import React, { useCallback } from 'react';
import Select from '../ui/Select';
import RangeControl from '../ui/RangeControl';
import Switch from '../ui/Switch';
import VideoInputRangeControls from './VideoInputRangeControls';
import { TopazVideoUpscaleModel, TopazVideoUpscaleParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { numberOrDefault } from '#lib/utils';
import { AppTooltip } from '../ui/tooltip';

interface TopazVideoUpscaleControlPanelProps {
  params: TopazVideoUpscaleParams;
  setModelParams: (params: (prevParams: { [model: string]: TopazVideoUpscaleParams }) => { [model: string]: TopazVideoUpscaleParams }) => void;
  diffusionModel: string;
}

const defaultTopazVideoUpscaleParams: TopazVideoUpscaleParams = {
  prompt: '',
  model: 'Proteus',
  upscale_factor: 2,
  target_fps: 30,
  compression: 0,
  noise: 0,
  halo: 0,
  grain: 0,
  recover_detail: 0,
  // ON by default: with it off Topaz returns HEVC (hvc1), which browsers largely cannot
  // demux. Measured 2026-08-13 on a real Chrome: an h264 640x360 source came back as
  // hevc 1280x720, frame extraction found 0 frames and the card could not play it. The
  // upscale was correct and the deliverable was still unusable. Turn it off only when the
  // output is destined for an editing suite rather than the app. AGENTS.md 4.6: enforce
  // what the user actually needs app-side rather than trusting the provider default.
  H264_output: true,
  video_input_start_ms: null,
  video_input_end_ms: null,
  seed: -1,
};

export const getDefaultTopazVideoUpscaleParams = (): TopazVideoUpscaleParams => ({
  ...defaultTopazVideoUpscaleParams,
});

const topazModelOptions: TopazVideoUpscaleModel[] = [
  'Proteus',
  'Artemis HQ',
  'Artemis MQ',
  'Artemis LQ',
  'Nyx',
  'Nyx Fast',
  'Nyx XL',
  'Nyx HF',
  'Gaia HQ',
  'Gaia CG',
  'Gaia 2',
  'Starlight Precise 1',
  'Starlight Precise 2',
  'Starlight Precise 2.5',
  'Starlight HQ',
  'Starlight Mini',
  'Starlight Sharp',
  'Starlight Fast 1',
  'Starlight Fast 2',
];

const TOPAZ_VIDEO_MODEL_TIP = 'Video enhancement model. Proteus is best for most videos, Artemis for denoise+sharpen, Nyx for dedicated denoising, Gaia HQ/CG for rendered content, Gaia 2 for animation and motion graphics at 2x, and Starlight for generative diffusion-based upscaling and enhancement.';
const TOPAZ_VIDEO_TOOLTIP_CLASS = 'max-w-[320px] border border-white/[0.06] bg-[#353535] px-3 py-2 text-xs leading-relaxed text-white/75 shadow-2xl';

const TopazVideoUpscaleControlPanel: React.FC<TopazVideoUpscaleControlPanelProps> = React.memo(({
  params,
  setModelParams,
  diffusionModel,
}) => {
  const { kernel } = useKernel();
  const resolvedModel = params.model ?? 'Proteus';
  const resolvedUpscaleFactor = numberOrDefault(params.upscale_factor, 2);
  const resolvedTargetFps = numberOrDefault(params.target_fps, 30);
  const resolvedCompression = numberOrDefault(params.compression, 0);
  const resolvedNoise = numberOrDefault(params.noise, 0);
  const resolvedHalo = numberOrDefault(params.halo, 0);
  const resolvedGrain = numberOrDefault(params.grain, 0);
  const resolvedRecoverDetail = numberOrDefault(params.recover_detail, 0);

  const updateParams = useCallback((
    name: keyof TopazVideoUpscaleParams,
    value: string | number | boolean | null
  ) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[TopazVideoUpscaleControlPanel] Missing params for model "${diffusionModel}".`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'TopazVideoUpscaleControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  const handleModelChange = useCallback((value: string) => {
    updateParams('model', value as TopazVideoUpscaleModel);
  }, [updateParams]);

  const handleTargetFpsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    updateParams('target_fps', Number(event.target.value));
  }, [updateParams]);

  const handleUpscaleFactorChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    updateParams('upscale_factor', Number(event.target.value));
  }, [updateParams]);

  const handleFloatChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateParams(name as keyof TopazVideoUpscaleParams, Number(value));
  }, [updateParams]);

  const handleH264Toggle = useCallback(() => {
    updateParams('H264_output', !(params.H264_output === true));
  }, [params.H264_output, updateParams]);

  const handleVideoInputRangeChange = useCallback((
    name: 'video_input_start_ms' | 'video_input_end_ms',
    value: number | null,
  ) => {
    updateParams(name, value);
  }, [updateParams]);

  return (
    <div className="space-y-4">
      <AppTooltip
        content={TOPAZ_VIDEO_MODEL_TIP}
        side="left"
        contentClassName={TOPAZ_VIDEO_TOOLTIP_CLASS}
      >
        <div>
          <Select
            label="Enhancement Model"
            value={resolvedModel}
            options={topazModelOptions}
            onChange={handleModelChange}
          />
        </div>
      </AppTooltip>
      <RangeControl
        label="Upscale Factor"
        name="upscale_factor"
        min={1}
        max={4}
        step={0.1}
        value={resolvedUpscaleFactor}
        onChange={handleUpscaleFactorChange}
        description="2.0 doubles width and height."
      />
      <RangeControl
        label="Target FPS"
        name="target_fps"
        min={1}
        max={120}
        step={1}
        value={resolvedTargetFps}
        onChange={handleTargetFpsChange}
        description="If set, frame interpolation is enabled."
      />
      <RangeControl
        label="Compression Removal"
        name="compression"
        min={0}
        max={1}
        step={0.01}
        value={resolvedCompression}
        onChange={handleFloatChange}
      />
      <RangeControl
        label="Noise Reduction"
        name="noise"
        min={0}
        max={1}
        step={0.01}
        value={resolvedNoise}
        onChange={handleFloatChange}
      />
      <RangeControl
        label="Halo Reduction"
        name="halo"
        min={0}
        max={1}
        step={0.01}
        value={resolvedHalo}
        onChange={handleFloatChange}
      />
      <RangeControl
        label="Film Grain"
        name="grain"
        min={0}
        max={0.1}
        step={0.005}
        value={resolvedGrain}
        onChange={handleFloatChange}
      />
      <RangeControl
        label="Recover Detail"
        name="recover_detail"
        min={0}
        max={1}
        step={0.01}
        value={resolvedRecoverDetail}
        onChange={handleFloatChange}
      />
      <VideoInputRangeControls
        startMs={params.video_input_start_ms}
        endMs={params.video_input_end_ms}
        onChange={handleVideoInputRangeChange}
      />
      <div className="flex items-center space-x-2">
        <Switch
          checked={params.H264_output === true}
          onCheckedChange={handleH264Toggle}
          labelRight="H264 Output"
        />
      </div>
      <p className="text-xs text-[#777777]">
        Upscales the highest visible video track at the current frame, or a composed visible timeline range when start/end times are provided. Reference images and prompts are ignored for this workflow.
      </p>
    </div>
  );
});

TopazVideoUpscaleControlPanel.displayName = 'TopazVideoUpscaleControlPanel';

export default TopazVideoUpscaleControlPanel;
